import React, { useMemo } from 'react';
import { Row, Col } from 'react-bootstrap';
import { formatCurrency } from '../../utility';
import { FaBoxOpen, FaCoins, FaChartLine, FaTag } from 'react-icons/fa';

const StockItemStats = ({ item }) => {
  const stats = useMemo(() => {
    if (!item) return { costVal: 0, sellVal: 0, margin: 0, profit: 0 };

    const availableImeis = (item.imeis || []).filter(s => s.status === 'Available');
    const imeiCost = availableImeis.reduce((s, i) => s + (parseFloat(i.unitCost) || 0), 0);
    const imeiSell = availableImeis.reduce((s, i) => s + (parseFloat(i.sellingPrice) || 0), 0);
    const batchCost = (item.batches || []).reduce((s, b) => s + ((b.availableQty || 0) * (parseFloat(b.unitCost) || 0)), 0);
    const batchSell = (item.batches || []).reduce((s, b) => s + ((b.availableQty || 0) * (parseFloat(b.sellingPrice) || 0)), 0);

    const totalCostVal = imeiCost + batchCost;
    const totalSellVal = imeiSell + batchSell;
    const profit = totalSellVal - totalCostVal;
    const margin = totalSellVal > 0 ? (profit / totalSellVal) * 100 : 0;

    return { costVal: totalCostVal, sellVal: totalSellVal, margin: margin.toFixed(1), profit };
  }, [item]);

  const summary = item?.stockSummary || { totalPurchased: 0, available: 0, sold: 0, damaged: 0 };

  return (
    <Row className="g-2">
      {/* 1. STOCK UNITS */}
      <Col xs={6} md={3}>
        <div
          className="p-2.5 p-md-3 rounded-3 border h-100 bg-white shadow-sm d-flex flex-column justify-content-between"
          style={{ borderColor: "#e2e8f0" }}
        >
          <div className="d-flex align-items-center justify-content-between mb-1">
            <span className="text-muted fw-bold" style={{ fontSize: "0.68rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
              Available Stock
            </span>
            <div className="d-flex align-items-center justify-content-center rounded-2" style={{ width: 24, height: 24, background: "#ecfdf5", color: "#10b981" }}>
              <FaBoxOpen size={12} />
            </div>
          </div>
          <div>
            <div className="d-flex align-items-baseline gap-1">
              <span className="fw-black text-dark" style={{ fontSize: "1.2rem", letterSpacing: "-0.02em" }}>
                {summary.available}
              </span>
              <span className="text-muted small fw-medium">Units</span>
            </div>
            <div className="d-flex align-items-center gap-1 mt-1 text-muted" style={{ fontSize: "0.68rem" }}>
              <span>Purchased: {summary.totalPurchased}</span>
              <span>•</span>
              <span className="text-success">Sold: {summary.sold}</span>
              {summary.damaged > 0 && (
                <>
                  <span>•</span>
                  <span className="text-danger">Damaged: {summary.damaged}</span>
                </>
              )}
            </div>
          </div>
        </div>
      </Col>

      {/* 2. ASSET VALUE */}
      <Col xs={6} md={3}>
        <div
          className="p-2.5 p-md-3 rounded-3 border h-100 bg-white shadow-sm d-flex flex-column justify-content-between"
          style={{ borderColor: "#e2e8f0" }}
        >
          <div className="d-flex align-items-center justify-content-between mb-1">
            <span className="text-muted fw-bold" style={{ fontSize: "0.68rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
              Asset Value (Cost)
            </span>
            <div className="d-flex align-items-center justify-content-center rounded-2" style={{ width: 24, height: 24, background: "#fef3c7", color: "#d97706" }}>
              <FaCoins size={12} />
            </div>
          </div>
          <div>
            <div className="fw-black text-dark" style={{ fontSize: "1.05rem", letterSpacing: "-0.01em" }}>
              {formatCurrency(stats.costVal)}
            </div>
            <div className="text-muted mt-1" style={{ fontSize: "0.68rem" }}>
              Unit Cost: <span className="fw-semibold text-dark">{formatCurrency(item?.lastUnitCost || 0)}</span>
            </div>
          </div>
        </div>
      </Col>

      {/* 3. POTENTIAL SALES */}
      <Col xs={6} md={3}>
        <div
          className="p-2.5 p-md-3 rounded-3 border h-100 bg-white shadow-sm d-flex flex-column justify-content-between"
          style={{ borderColor: "#e2e8f0" }}
        >
          <div className="d-flex align-items-center justify-content-between mb-1">
            <span className="text-muted fw-bold" style={{ fontSize: "0.68rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
              Potential Sales
            </span>
            <div className="d-flex align-items-center justify-content-center rounded-2" style={{ width: 24, height: 24, background: "#f0f9ff", color: "#0284c7" }}>
              <FaTag size={12} />
            </div>
          </div>
          <div>
            <div className="fw-black text-dark" style={{ fontSize: "1.05rem", letterSpacing: "-0.01em" }}>
              {formatCurrency(stats.sellVal)}
            </div>
            <div className="text-muted mt-1" style={{ fontSize: "0.68rem" }}>
              Last Sell Price: <span className="fw-semibold text-dark">{formatCurrency(item?.lastPurchaseSellingPrice || item?.pricing?.sellingPrice || 0)}</span>
            </div>
          </div>
        </div>
      </Col>

      {/* 4. EST MARGIN */}
      <Col xs={6} md={3}>
        <div
          className="p-2.5 p-md-3 rounded-3 border h-100 bg-white shadow-sm d-flex flex-column justify-content-between"
          style={{ borderColor: "#e2e8f0" }}
        >
          <div className="d-flex align-items-center justify-content-between mb-1">
            <span className="text-muted fw-bold" style={{ fontSize: "0.68rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
              Est. Margin
            </span>
            <div className="d-flex align-items-center justify-content-center rounded-2" style={{ width: 24, height: 24, background: "#ecfdf5", color: "#059669" }}>
              <FaChartLine size={12} />
            </div>
          </div>
          <div>
            <div className="d-flex align-items-baseline gap-1.5">
              <span className="fw-black text-emerald-600" style={{ fontSize: "1.15rem", color: "#059669", letterSpacing: "-0.02em" }}>
                {stats.margin}%
              </span>
              {stats.profit > 0 && (
                <span className="badge rounded-pill bg-success bg-opacity-10 text-success" style={{ fontSize: "0.65rem", padding: "2px 6px" }}>
                  +{formatCurrency(stats.profit)}
                </span>
              )}
            </div>
            <div className="text-muted mt-1" style={{ fontSize: "0.68rem" }}>
              Expected return on current stock
            </div>
          </div>
        </div>
      </Col>
    </Row>
  );
};

export default StockItemStats;
